import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useFormikContext } from 'formik';
import { Service } from '../../interfaces';
import CheckoutItemsSearch from './CheckoutItemsSearch';
import {
  CheckoutFormValues,
  SelectedAppointment,
  ServiceWithHourlyPrice,
} from '../../types/appointments';
import { convertSnakeToTitleCase } from '../../utils/global/helper';
import useUser from '../../hooks/global/useUser';
import {
  useServiceFormModal,
  buildServiceCheckoutItem,
} from '../../hooks/checkouts/useServiceFormModal';
import { AdditionalServiceCheckoutItem } from '../../types/payments';
import {
  groupServicesByCategory,
  isAppointmentDepositPaid,
} from '../../helpers/appointments';
import useGiftCardsEnabled from '../../hooks/checkouts/useGiftCardsEnabled';
import { AddItemsTab } from '../../hooks/checkouts/useCheckoutPricing';
import useInventoryEnabled from '../../hooks/businesses/useInventoryEnabled';
import CatalogPicker from './catalog/CatalogPicker';

interface Props {
  open: boolean;
  onClose: () => void;
  services: Service[] | undefined;
  appointment: SelectedAppointment | null;
  /** Tab to land on; defaults to the first one available. */
  initialTab?: AddItemsTab;
}

export default function AddServiceOrItemModal({
  open,
  onClose,
  services,
  appointment,
  initialTab,
}: Props) {
  const theme = useTheme();
  const { user } = useUser();
  const showServiceForm = useServiceFormModal();
  const { values, setFieldValue } = useFormikContext<CheckoutFormValues>();
  const hasAppointment = !!appointment;
  const defaultTattooDeposit = user?.business?.defaultTattooDeposit ?? null;
  const relevantEmployeeId =
    appointment?.employeeId ??
    user?.employee?.employeeId ??
    user?.userId ??
    null;
  const relevantEmployeeHourlyRate =
    user?.business?.employees?.find(e => e.employeeId === relevantEmployeeId)
      ?.defaultHourlyRate ?? null;
  const giftCardsEnabled = useGiftCardsEnabled({
    customer: values.customer,
    appointment,
    enabled: open,
  });
  // Same rule as the legacy search: another location's appointment can't use this shop's library
  const sameBusiness =
    !appointment ||
    (!!user?.business?.id && user.business.id === appointment.businessId);
  const useCatalogPicker = useInventoryEnabled() && sameBusiness;
  const tabKeys = React.useMemo(() => {
    const keys: AddItemsTab[] = [];
    if (hasAppointment) keys.push('services');
    keys.push('items');
    if (giftCardsEnabled) keys.push('giftCards');
    return keys;
  }, [hasAppointment, giftCardsEnabled]);
  const [tab, setTab] = React.useState<AddItemsTab>(tabKeys[0]);
  React.useEffect(() => {
    if (!open) return;
    setTab(
      initialTab && tabKeys.includes(initialTab) ? initialTab : tabKeys[0],
    );
  }, [open, initialTab, tabKeys]);
  const [expandedCategory, setExpandedCategory] = React.useState<
    string | false
  >(false);

  const categoriesMap = React.useMemo(
    () =>
      groupServicesByCategory(
        services,
        defaultTattooDeposit,
        relevantEmployeeHourlyRate,
      ),
    [services, defaultTattooDeposit, relevantEmployeeHourlyRate],
  );
  const categories = React.useMemo(
    () => Array.from(categoriesMap.keys()),
    [categoriesMap],
  );

  const handleServiceClick = async (service: ServiceWithHourlyPrice) => {
    onClose();
    const defaultEmployeeId = user?.employee?.employeeId ?? user?.userId ?? '';
    // Adding a NEW line — the cart's existing total IS the "other lines."
    const depositPaid =
      appointment && isAppointmentDepositPaid(appointment)
        ? appointment.depositPrice ?? 0
        : 0;
    const mainBase = Math.max(0, (values.price ?? 0) - depositPaid);
    const additionalTotal = (values.additionalServices ?? []).reduce(
      (acc, s) => acc + (s.price ?? 0),
      0,
    );
    const itemsTotal = (values.customOrderItems ?? []).reduce(
      (acc, i) => acc + (i.price ?? 0) * (i.quantity ?? 1),
      0,
    );
    const groupOtherLinesTotal = mainBase + additionalTotal + itemsTotal;
    const result = await showServiceForm({
      service,
      user: user ?? null,
      appointmentEmployeeId: appointment?.employeeId,
      paymentMethod: values.paymentMethod,
      groupOtherLinesTotal,
    });
    if (!result) return;
    const built = buildServiceCheckoutItem(service, result, defaultEmployeeId);
    const newItem: AdditionalServiceCheckoutItem = {
      ...built,
      id: `add-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    };
    setFieldValue('additionalServices', [
      ...(values.additionalServices ?? []),
      newItem,
    ]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={useCatalogPicker && tab === 'items' ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
        },
      }}>
      <DialogContent sx={{ p: 0 }}>
        {tabKeys.length > 1 && (
          <>
            <Box sx={{ px: 3, mt: 2 }}>
              <Tabs
                value={tab}
                onChange={(_e, v) => setTab(v)}
                sx={{
                  minHeight: 36,
                  '& .MuiTabs-root': { minHeight: 36 },
                  '& .MuiTabs-indicator': {
                    height: 2,
                    borderRadius: '2px 2px 0 0',
                    backgroundColor: theme.palette.primary.main,
                  },
                  '& .MuiTabs-flexContainer': { gap: 0.5 },
                }}>
                {tabKeys.map(key => (
                  <Tab
                    key={key}
                    value={key}
                    label={
                      key === 'services'
                        ? 'Services'
                        : key === 'items'
                        ? 'Items'
                        : 'Gift Cards'
                    }
                    disableRipple
                    sx={{
                      minHeight: 36,
                      py: 0,
                      px: 1.5,
                      ...theme.typography.body1,
                      textTransform: 'none',
                      color: theme.palette.text.secondary,
                      '&.Mui-selected': { color: theme.palette.text.primary },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
            <Divider />
          </>
        )}

        {/* Tab panels */}
        <Box sx={{ px: 2.5, py: 2 }}>
          {hasAppointment && tab === 'services' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {categories.map(category => {
                const list = categoriesMap.get(category) ?? [];
                const isExpanded = expandedCategory === category;
                return (
                  <Box
                    key={category}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'box-shadow 0.15s ease',
                      '&:hover': {
                        boxShadow: isExpanded
                          ? '0 2px 8px rgba(0,0,0,0.06)'
                          : '0 1px 4px rgba(0,0,0,0.04)',
                      },
                    }}>
                    {/* Category header */}
                    <Box
                      component="button"
                      type="button"
                      onClick={() =>
                        setExpandedCategory(isExpanded ? false : category)
                      }
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1.375,
                        px: 1.75,
                        border: 'none',
                        background: isExpanded
                          ? alpha(theme.palette.primary.main, 0.04)
                          : theme.palette.background.paper,
                        cursor: 'pointer',
                        textAlign: 'left',
                        font: 'inherit',
                        color: 'inherit',
                        transition: 'background 0.15s ease',
                        '&:hover': {
                          background: isExpanded
                            ? alpha(theme.palette.primary.main, 0.06)
                            : alpha(theme.palette.grey[500], 0.05),
                        },
                      }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            color: isExpanded
                              ? theme.palette.primary.main
                              : theme.palette.text.primary,
                            transition: 'color 0.15s ease',
                          }}>
                          {convertSnakeToTitleCase(category)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.disabled,
                            lineHeight: 1,
                            px: 0.75,
                            py: 0.375,
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            borderRadius: '6px',
                            fontWeight: 600,
                          }}>
                          {list.length}
                        </Typography>
                      </Box>
                      <ExpandMoreRoundedIcon
                        sx={{
                          transform: isExpanded
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                          color: isExpanded
                            ? theme.palette.primary.main
                            : theme.palette.text.disabled,
                          fontSize: '1.25rem',
                        }}
                      />
                    </Box>

                    {/* Service list */}
                    {isExpanded && (
                      <Box
                        sx={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                          py: 0.75,
                          px: 0.75,
                          maxHeight: 240,
                          overflowY: 'auto',
                          bgcolor: alpha(theme.palette.grey[500], 0.02),
                          '&::-webkit-scrollbar': { width: 4 },
                          '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: alpha(theme.palette.grey[500], 0.25),
                            borderRadius: 2,
                          },
                        }}>
                        {list.map((service, idx) => (
                          <Box
                            key={service.serviceId}
                            onClick={() => handleServiceClick(service)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 1.125,
                              px: 1.5,
                              cursor: 'pointer',
                              borderRadius: '8px',
                              mt: idx === 0 ? 0 : 0.25,
                              transition: 'background 0.12s ease',
                              '&:hover': {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.07,
                                ),
                              },
                              '&:active': {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.12,
                                ),
                              },
                            }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: theme.palette.text.primary,
                              }}>
                              {service.serviceName}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color:
                                  service.defaultHourlyPrice != null ||
                                  service.defaultPrice > 0
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                                flexShrink: 0,
                                ml: 1,
                              }}>
                              {service.defaultHourlyPrice != null
                                ? 'Variable'
                                : service.defaultPrice > 0
                                ? `$${service.defaultPrice.toFixed(2)}`
                                : 'Set in checkout'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {tab === 'items' && useCatalogPicker && (
            <CatalogPicker onAdded={onClose} />
          )}

          {((tab === 'items' && !useCatalogPicker) || tab === 'giftCards') && (
            <Box>
              <CheckoutItemsSearch
                appointment={appointment ?? undefined}
                onItemAdded={onClose}
                autoFocus={open && tab === 'items'}
                includeGiftCards={false}
                giftCardsOnly={tab === 'giftCards'}
                hideSearch={tab === 'giftCards'}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
